import React, { useState, useEffect, useMemo } from "react";
import {
    Table,
    TableHeader,
    TableRow,
    TableHeaderCell,
    TableBody,
    TableCell,
    TableCellLayout,
    Button,
    Input,
    useTableFeatures,
    useTableSort,
    createTableColumn,
} from "@fluentui/react-components";
import { CiFilter } from "react-icons/ci";
import { DeleteRegular, Add20Filled, EditRegular } from "@fluentui/react-icons";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import "../CSS/ListVariablesComponent.css";
import { getAccessToken } from "../../Services/GraphServices";
import { deleteVariable } from "../../Services/HeedifyServices";
import { ConfirmAlert } from "../GenericComponent/ConfirmAlert";
import { useNavigate } from "react-router-dom";
import { useAbility } from "../../Contexts/AbilityContext";
import InfoButton from "../GenericComponent/InfoButton";

const ListVariablesComponent = (props) => {
    const { instance, accounts } = useMsal();
    const activeAccount = accounts[0];
    const userId = activeAccount?.localAccountId;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const ability = useAbility();

    const [variablesRows, setVariablesRows] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [selectedVariable, setSelectedVariable] = useState(null);
    const [filterVisible, setFilterVisible] = useState({
        NAME: false,
        SCOPE: false,
        CUSTOM_DATA: false,
    });
    // Filtres individuels
    const [filters, setFilters] = useState({
        NAME: "",
        SCOPE: "",
        CUSTOM_DATA: "",
    });

    useEffect(() => {
        if (props.VARIABLES) {
            // 🔐 Calculer permissions pour chaque variable
            const variablesWithPermissions = props.VARIABLES.map(variable => ({
                ...variable,
                permissions: {
                    main: {
                        canCreate: ability?.can("create", `CustomerDB.main@${variable.scope}`),
                        canRead: ability?.can("read", `CustomerDB.main@${variable.scope}`),
                        canUpdate: ability?.can("update", `CustomerDB.main@${variable.scope}`),
                        canDelete: ability?.can("delete", `CustomerDB.main@${variable.scope}`),
                    },
                },
            }));
            setVariablesRows(variablesWithPermissions);
            setFilteredRows(variablesWithPermissions);
        }
    }, [props.VARIABLES, ability]);
    // --- Fonction de filtrage externe ---
    const applyFilters = (variablesRows, filters) => {
        return variablesRows.filter((variable) => {
            const matchName = variable.displayName
                ?.toLowerCase()
                .includes(filters.NAME.toLowerCase());
            const matchScope = variable.scope
                ?.toLowerCase()
                .includes(filters.SCOPE.toLowerCase());
            const keysCount = variable.DB ? Object.keys(variable.DB).length : 0;
            const matchCustomData = keysCount.toString().startsWith(filters.CUSTOM_DATA);
            // 🔐 Filtrer par permission canRead
            const hasReadPermission = variable.permissions?.main?.canRead;

            return matchName && matchScope && matchCustomData && hasReadPermission;
        });
    };

    // --- useEffect qui applique le filtre ---
    useEffect(() => {
        const newFiltered = applyFilters(variablesRows, filters);
        setFilteredRows(newFiltered);
    }, [filters, variablesRows]);

    // --- Tri avec Sorting ---
    const columns = [
        createTableColumn({
            columnId: "NAME",
            compare: (a, b) => a.displayName?.localeCompare(b.displayName || "") ?? 0,
        }),
        createTableColumn({
            columnId: "SCOPE",
            compare: (a, b) => a.scope?.localeCompare(b.scope || "") ?? 0,
        }),
        createTableColumn({
            columnId: "CUSTOM_DATA",
            compare: (a, b) => {
                const aCount = a.DB ? Object.keys(a.DB).length : 0;
                const bCount = b.DB ? Object.keys(b.DB).length : 0;
                return aCount - bCount;
            },
        }),
        createTableColumn({ columnId: "ACTION" }),
    ];

    const {
        getRows,
        sort: { getSortDirection, toggleColumnSort, sort },
    } = useTableFeatures({ columns, items: filteredRows }, [
        useTableSort({
            defaultSortState: { sortColumn: "NAME", sortDirection: "ascending" },
        }),
    ]);

    /* const rows =   sort(getRows());*/
    const rows = useMemo(() => {
        return sort(getRows());
    }, [sort, getRows]);
    const headerSortProps = (columnId) => ({
        onClick: (e) => toggleColumnSort(e, columnId),
        sortDirection: getSortDirection(columnId),
    });

    const handleFilterChange = (columnKey, value) => {
        setFilters((prev) => ({ ...prev, [columnKey]: value }));
    };

    // Toggle the visibility of a filter input field
    const toggleFilterVisibility = (column) => {
        setFilterVisible((prev) => ({ ...prev, [column]: !prev[column] }));
    };
    // --- Suppression ---
    const removeVariable = (key, variableId, scope) => {
        getAccessToken(instance, activeAccount)
            .then((token) => {
                deleteVariable(props.ACTIVE_TENANT, scope, variableId, token)
                    .then((response) => {
                        if (response && response.success === true && response.message === "deleted") {
                            // Update the global variables state
                            props.setVariables(props.VARIABLES.filter(variable => variable.id !== variableId));
                            setVariablesRows((prev) => prev.filter((_, i) => i !== key));
                        } else if (response) {
                            console.error("❌ Failed to delete variable:", response.message);
                        }
                    })
            })
            .catch((error) => console.error("❌ Error deleting variable:", error))
            .finally(() => setSelectedVariable(null));
    };

    const confirmRemoveVariable = (variable, index) => {
        setSelectedVariable({ ...variable, key: index });
    };

    const addVariable = () => {
        navigate(`/variables/add`);
    };
    const selectVariable = (event, data) => {
        navigate(`/variables/${data}`);
    };

    // 🔐 Vérifier si on peut créer une variable dans au moins un scope
    const hasAnyCreatePermission = props.SCOPES_NAMES?.some(scope =>
        ability?.can("create", `CustomerDB.main@${scope}`)
    );

    return (
        <>
            <div>
                <div className="variable-list-header-container">
                    <h2>{t("VARIABLES_LIST")}</h2>
                    <Button
                        appearance="primary"
                        onClick={addVariable}
                        className="variable-list-button-style"
                        disabled={!hasAnyCreatePermission}
                    >
                        <Add20Filled /> {t("ADD_NEW_VARIABLE")}
                    </Button>
                </div>

                <div className="table-container">
                    <div className="variable-list-table-wrapper">
                        <Table
                            role="grid"
                            aria-label="Variables table with filters and sorting"
                            className="variable-list-table-style"
                            sortable
                        >
                            <TableHeader>
                                {/* Ligne des titres triables */}
                                <TableRow className="variable-list-header-style">
                                    <TableHeaderCell className="variable-list-cell-bold" {...headerSortProps("NAME")}>
                                        <div className="header-filter-container">
                                            <div
                                                className="div-filter-style"
                                            >
                                                {t("NAME")}
                                                <CiFilter
                                                    className="filter-icon"
                                                    onClick={(e) => {

                                                        e.stopPropagation(); // Empêche le clic de déclencher le tri
                                                        toggleFilterVisibility("NAME");
                                                    }}
                                                />
                                            </div>{" "}
                                            {filterVisible.NAME && (
                                                <Input
                                                    className="input-filter-style"
                                                    value={filters.NAME}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e, data) =>
                                                        handleFilterChange("NAME", data.value)
                                                    }
                                                    placeholder={t("FILTER")}
                                                    size="small"
                                                />
                                            )}
                                        </div>
                                    </TableHeaderCell>
                                    <TableHeaderCell className="variable-list-cell-bold" {...headerSortProps("SCOPE")}>
                                        <div className="header-filter-container"><div
                                            className="div-filter-style"

                                        >
                                            {" "}
                                            {t("SCOPE")}
                                            <CiFilter
                                                className="filter-icon"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    toggleFilterVisibility("SCOPE");
                                                }}
                                            />
                                        </div>{" "}
                                            {filterVisible.SCOPE && (
                                                <Input
                                                    className="input-filter-style"
                                                    value={filters.SCOPE}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e, data) =>
                                                        handleFilterChange("SCOPE", data.value)
                                                    }
                                                    placeholder={t("FILTER")}
                                                    size="small"
                                                />
                                            )}
                                        </div>
                                    </TableHeaderCell>
                                    <TableHeaderCell className="variable-list-cell-bold" {...headerSortProps("CUSTOM_DATA")}>
                                        <div className="header-filter-container">
                                            <div
                                                className="div-filter-style"
                                            >
                                                {" "}
                                                {t("CUSTOM_DATA")} {t("KEYS")}
                                                <InfoButton infoKey="CUSTOM_DATA" category="VARIABLES" />
                                                <CiFilter
                                                    className="filter-icon"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleFilterVisibility("CUSTOM_DATA");
                                                    }}
                                                />
                                            </div>{" "}
                                            {filterVisible.CUSTOM_DATA && (
                                                <Input
                                                    className="input-filter-style"
                                                    value={filters.CUSTOM_DATA}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e, data) =>
                                                        handleFilterChange("CUSTOM_DATA", data.value)
                                                    }
                                                    placeholder={t("FILTER")}
                                                    size="small"
                                                />
                                            )}
                                        </div>
                                    </TableHeaderCell>
                                    <TableHeaderCell className="variable-list-cell-bold agent-profile-list-cell-right">{t("ACTION")}</TableHeaderCell>
                                </TableRow>
                            </TableHeader>

                            <TableBody >
                                {rows.map(({ item, rowId }) => {
                                    const keysCount = item.DB ? Object.keys(item.DB).length : 0;
                                    return (
                                        <TableRow key={rowId}>
                                            <TableCell>
                                                <TableCellLayout>{item.displayName}</TableCellLayout>
                                            </TableCell>
                                            <TableCell>
                                                <TableCellLayout>{item.scope}</TableCellLayout>
                                            </TableCell>
                                            <TableCell>
                                                <TableCellLayout>{keysCount} </TableCellLayout>
                                            </TableCell>
                                            <TableCell className="variable-list-cell-right">
                                                <Button
                                                    appearance = "subtle"
                                                    icon={<DeleteRegular />}
                                                    aria-label="Delete"
                                                    onClick={() => confirmRemoveVariable(item, rowId)}
                                                    disabled={!item.permissions?.main?.canDelete}
                                                />
                                                <Button
                                                    appearance = "subtle"
                                                    icon={<EditRegular />}
                                                    aria-label="Edit"
                                                    onClick={(event) => selectVariable(event, item.id)}
                                                    disabled={!item.permissions?.main?.canUpdate}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </div>

                {selectedVariable && (
                    <ConfirmAlert
                        open={!!selectedVariable}
                        name={selectedVariable.displayName}
                        onConfirm={() =>
                            removeVariable(
                                selectedVariable.key,
                                selectedVariable.id,
                                selectedVariable.scope
                            )
                        }
                        onCancel={() => setSelectedVariable(null)}
                    />
                )}
            </div>
        </>
    );
};

export default ListVariablesComponent;
